import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import _ from 'lodash';
import { denormalize } from 'normalizr';
import { SCHEMAS } from 'js/api';

import {
  load as loadTeam,
  update as updateTeam,
  addMember as addTeamMember,
  updateMember as updateTeamMember,
  removeMember as removeTeamMember,
} from 'js/actions/team-actions';
import {
  list as listAssessments,
  update as updateAssessment,
} from 'js/actions/assessment-actions';
import { load as loadGoals } from 'js/actions/goal-actions';
import { notify } from 'js/actions/alert-actions';
import { listConnectors } from 'js/actions/connector-actions';
import {
  listTeamConnectorConfigurations,
  createTeamConnectorConfiguration,
  updateTeamConnectorConfiguration,
  deleteTeamConnectorConfiguration,
} from 'js/actions/team-connector-configuration-actions';
import TeamDashboardComponent from '../components/TeamDashboardComponent';
import { useQuery } from 'js/hooks/useQuery';

function TeamDashboardContainer(props) {
  const {
    goals,
    team,
    assessments,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    query,
    queryId,
    loadTeam,
    loadGoals,
    match,
    listAssessments,
    listConnectors,
    listTeamConnectorConfigurations,
    setPrimaryGoal,
    updateAssessment,
    updateTeam,
    notify,
    createTeamConnectorConfiguration,
    updateTeamConnectorConfiguration,
    deleteTeamConnectorConfiguration,
  } = props;

  const { teamId, workspaceId } = match.params;

  // Load data on mount and when params change
  useEffect(() => {
    loadTeam(teamId, queryId);
    loadGoals();
    listAssessments({ team_id: teamId });
  }, [
    teamId,
    workspaceId,
    queryId,
    loadTeam,
    loadGoals,
    listAssessments,
    listConnectors,
    listTeamConnectorConfigurations,
  ]);

  const refreshConnectorsQuery = useCallback(
    (queryId) => listConnectors(workspaceId, queryId),
    [workspaceId, listConnectors]
  );

  const {
    result: connectors,
    refresh: refreshConnectors,
    hasLoaded: connectorsHasLoaded,
  } = useQuery({
    queryId: `connectors-${workspaceId}`,
    query: refreshConnectorsQuery,
    schema: SCHEMAS.CONNECTOR_ARRAY,
  });

  // Load all team connector configurations to identify which connectors are already configured
  const refreshTeamConnectorConfigurationsQuery = useCallback(
    (queryId) => listTeamConnectorConfigurations(teamId, queryId),
    [teamId, listTeamConnectorConfigurations]
  );

  const {
    result: teamConnectorConfigurations,
    refresh: refreshTeamConnectorConfigurations,
    hasLoaded: teamConnectorConfigurationsHasLoaded,
  } = useQuery({
    queryId: `teamConnectorConfigurations-${teamId}`,
    query: refreshTeamConnectorConfigurationsQuery,
    schema: SCHEMAS.TEAM_CONNECTOR_CONFIGURATION_ARRAY,
  });

  useEffect(() => refreshConnectors(), [refreshConnectors]);

  useEffect(
    () => refreshTeamConnectorConfigurations(),
    [refreshTeamConnectorConfigurations]
  );

  // Memoized handlers
  const handleSetPrimary = useCallback(
    (team, goal) => {
      setPrimaryGoal(team.id, goal.id);
    },
    [setPrimaryGoal]
  );

  const handleToggleAssessmentLock = useCallback(
    (assessment) => {
      return updateAssessment({
        id: assessment.id,
        is_locked: !assessment.is_locked,
      });
    },
    [updateAssessment]
  );

  const handleDeleteAssessment = useCallback(
    (assessment) => {
      return updateAssessment({
        id: assessment.id,
        active: false,
      });
    },
    [updateAssessment]
  );

  const handleUpdateTeam = useCallback(
    (team) => {
      return updateTeam(team);
    },
    [updateTeam]
  );

  const handleCopyLink = useCallback(() => {
    notify({ type: 'success', message: 'Link copied to clipboard' });
  }, [notify]);

  const handleSaveConnectorConfiguration = useCallback(
    async (connectorId, configurationData, existingConfiguration = null) => {
      try {
        if (existingConfiguration) {
          await updateTeamConnectorConfiguration(teamId, connectorId, {
            configuration: configurationData,
          });
          notify({
            type: 'success',
            message: 'Connector configuration updated successfully',
          });
        } else {
          await createTeamConnectorConfiguration(teamId, {
            connector_id: connectorId,
            configuration: configurationData,
          });
          notify({
            type: 'success',
            message: 'Connector configuration created successfully',
          });
        }
      } catch (error) {
        notify({
          type: 'danger',
          message: 'Failed to save connector configuration',
        });
        throw error;
      }
    },
    [
      teamId,
      createTeamConnectorConfiguration,
      updateTeamConnectorConfiguration,
      notify,
    ]
  );

  const handleDeleteConnectorConfiguration = useCallback(
    async (connectorId) => {
      try {
        await deleteTeamConnectorConfiguration(teamId, connectorId);
        notify({
          type: 'success',
          message: 'Connector configuration deleted successfully',
        });
      } catch (error) {
        notify({
          type: 'danger',
          message: 'Failed to delete connector configuration',
        });
        throw error;
      }
    },
    [teamId, deleteTeamConnectorConfiguration, notify]
  );

  const handleTestConnectorConfiguration = useCallback(
    async (configurationData) => {
      // TODO: Implement test functionality
      console.log('Testing connector configuration:', configurationData);
    },
    []
  );

  return (
    <TeamDashboardComponent
      team={team}
      query={query}
      goals={goals}
      assessments={assessments}
      onToggleAssessmentLock={handleToggleAssessmentLock}
      onDeleteAssessment={handleDeleteAssessment}
      onAddTeamMember={addTeamMember}
      onUpdateTeamMember={updateTeamMember}
      onRemoveTeamMember={removeTeamMember}
      onSetPrimary={handleSetPrimary}
      onUpdateTeam={handleUpdateTeam}
      onCopyLink={handleCopyLink}
      connectors={connectors}
      connectorsHasLoaded={connectorsHasLoaded}
      teamConnectorConfigurations={teamConnectorConfigurations}
      teamConnectorConfigurationsHasLoaded={
        teamConnectorConfigurationsHasLoaded
      }
      onSaveConnectorConfiguration={handleSaveConnectorConfiguration}
      onDeleteConnectorConfiguration={handleDeleteConnectorConfiguration}
      onTestConnectorConfiguration={handleTestConnectorConfiguration}
    />
  );
}

export default connect(
  (store, props) => {
    const { match } = props;
    const { entities } = store;
    let { teamId, workspaceId } = match.params;
    teamId = +teamId;
    workspaceId = +workspaceId;

    const queryId = `w/${workspaceId}/t/${teamId}`;

    let team = entities.teams[teamId];
    if (!!team && team.workspace_id !== workspaceId) {
      team = undefined;
    }

    return {
      queryId,
      query: store.queries[queryId] || {},
      team,
      goals: _.map(entities.goals, (x) => x).sort(
        (a, b) => a.ordinal - b.ordinal
      ),
      assessments: _.chain(entities.assessments)
        .filter((a) => a.team_id === teamId && a.active)
        .orderBy(['name', 'inserted_at'], ['asc', 'desc'])
        .map((a) => denormalize(a, SCHEMAS.ASSESSMENT, entities))
        .value(),
    };
  },
  {
    loadGoals,
    loadTeam,
    addTeamMember,
    updateTeamMember,
    removeTeamMember,
    setPrimaryGoal: (teamId, goalId) => {
      return updateTeam({ id: teamId, primary_goal: goalId });
    },
    updateTeam,
    listAssessments,
    updateAssessment,
    notify,
    listConnectors,
    listTeamConnectorConfigurations,
    createTeamConnectorConfiguration,
    updateTeamConnectorConfiguration,
    deleteTeamConnectorConfiguration,
  }
)(TeamDashboardContainer);
